/**
 * RedeemPage — invite redemption (route `/redeem?token=…`).
 *
 * Story: epic-uat-portal-001-8.
 *
 * A tester arrives from an invite link carrying a one-time `token` in the query
 * string. They supply an email + display name; on success a lightweight account
 * is created, the session token is stored (in-memory), and they enter the portal.
 *
 * Error handling (plan + backend contract):
 *   - 401 invite_invalid → the link is bad/expired; tell them to request a new one.
 *   - 409 email_in_use   → the email already has an account; point at magic-link sign-in.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTesterAuth } from '@/auth/testerAuthContext';
import {
  redeemInvite,
  TesterAccountApiError,
} from '@/services/testerAccountService';
import { setRememberedEmail } from '@/auth/rememberedEmail';

export function RedeemPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { login } = useTesterAuth();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const session = await redeemInvite(token, email.trim(), displayName.trim());
      setRememberedEmail(email.trim());
      login(session.account, session.sessionToken);
      navigate('/app', { replace: true });
    } catch (err) {
      const message =
        err instanceof TesterAccountApiError
          ? err.message
          : 'Something went wrong redeeming your invite. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-8 text-center">
        <h1 className="text-2xl font-bold" data-testid="redeem-heading">
          Invite link incomplete
        </h1>
        <p className="text-muted-foreground" data-testid="redeem-missing-token">
          This invite link is missing its token. Please use the full link from
          your invitation, or <Link to="/login" className="underline">sign in</Link>{' '}
          if you already have an account.
        </p>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 p-8">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-2xl font-bold" data-testid="redeem-heading">
          Join the UAT testers
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Set up your lightweight tester account to report bugs and follow the
          status board.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="redeem-email"
              className="rounded border border-gray-300 px-3 py-2"
              autoComplete="email"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Display name</span>
            <input
              type="text"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="redeem-display-name"
              className="rounded border border-gray-300 px-3 py-2"
              autoComplete="nickname"
            />
          </label>

          {error && (
            <p
              role="alert"
              data-testid="redeem-error"
              className="text-sm text-red-600"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="redeem-submit"
            className="rounded bg-green-600 px-4 py-2 font-medium text-white disabled:opacity-60"
          >
            {submitting ? 'Setting up…' : 'Create account'}
          </button>
        </form>
      </div>
    </main>
  );
}
